import { css, cssom, defineConfig, dom, observe, twind } from '@twind/core';
import presetAutoprefix from '@twind/preset-autoprefix';
import presetTailwind from '@twind/preset-tailwind';
import 'construct-style-sheets-polyfill';
import { Property } from 'csstype';

import interFontMedium from './assets/fonts/Inter/Inter-Medium.woff2';
import interFontSemibold from './assets/fonts/Inter/Inter-SemiBold.woff2';
import interFont from './assets/fonts/Inter/Inter.woff2';
import { BackgroundStyleType, ButtonStyleType, GradientDirectionType, PlacementType } from './copied/types/ChatItems';
import { FeatureFlags } from './hooks/useFeatureFlags';
import { getConfig } from './services/config';
import pxFontSize from './startTwind/pxFontSize';
import pxSpacing from './startTwind/pxSpacing';
import { shadowRoot } from './shadowRoot';
import { HelpCenterResponse } from './services/helpCenter';
import { isAdoptedStyleSheetsSupported } from './utils/isAdoptedStyleSheetsSupported';
import { waitForIDs } from './copied/getIds';
import { ChatVersion } from './copied/constants/common';

export function getFonts(data: HelpCenterResponse) {
  if (!data.customization.fonts || data.customization.fonts.length === 0) {
    return [
      new FontFace('Inter', `url(${interFont})`),
      new FontFace('Inter', `url(${interFontSemibold})`, {
        weight: '600',
      }),
      new FontFace('Inter', `url(${interFontMedium})`, {
        weight: '500',
      }),
    ];
  }

  return data.customization.fonts.map(
    (font) =>
      new FontFace('OstroCustomFont', `url(${font.fontUrl})`, {
        weight: font.weight === 'regular' ? '400' : font.weight === 'semi-bold' ? '600' : '500',
      })
  );
}

export function createTwindConfig(data: HelpCenterResponse, flags: FeatureFlags, webChatVersion: ChatVersion) {
  const mainColor = flags.uiRefreshMainColorAsIs
    ? data.customization.main_color
    : `${data.customization.main_color.substring(0, 7)}40`;

  const theme = {
    fontFamily: {
      sans: (() => {
        if (!data.customization.fonts || data.customization.fonts.length === 0) {
          return 'Inter, sans-serif';
        }

        return 'OstroCustomFont, sans-serif';
      })(),
    },
    fontSize: {
      ...pxFontSize,
      sectionHeader: [
        `${data.customization.fe_config?.typography?.sectionHeader?.fontSize}px`,
        { lineHeight: `${(data.customization.fe_config?.typography?.sectionHeader?.fontSize || 0) + 6}px` },
      ],
    },

    spacing: pxSpacing,
    extend: {
      fontWeight: {
        sectionHeader: '400',
      },
      colors: {
        error: '#B30505',
        brand: {
          primary: mainColor,
          panelTop: '#FFFFFF',
          panelBottom: mainColor,
          closeButtonBackground: mainColor,
          closeButtonX: '#000000',
          openButtonBackground: data.customization.open_button_background_color,
          openBubbleBackground: data.customization.open_bubble_background_color,
          nextBestAction: data.next_best_action.highlight_color,
          nbaButtonBackground: data.next_best_action.open_nba_button_background_color,
          nbaFontColor: data.next_best_action.nba_font_color,
        },
        typography: {
          heading: '#1E1E1E',
          copy: '#363636',
          description: '#4B4D4D',
          secondary: '#575757',
          divider: '#AEAEAE',
          info: '#4F4F4F99',
          sectionHeader: data.customization.fe_config?.typography?.sectionHeader?.color,
          placeholder: '#4C5050',
        },
        option: '#F3F3F3',
        tag: '#EDEDED',
        pills: {
          borderColor: data.customization.fe_config?.pills?.borderColor,
          hoverBorderColor: data.customization.fe_config?.pills?.hoverBorderColor,
        },
        virtualAssistant: {
          optionBackgroundColor: '#F3F3F3',
          selectedOptionBackgroundColor: mainColor,
        },
      },
      height: {
        app: 'var(--app-height)',
      },
      width: {
        app: 'var(--app-width)',
      },
      borderRadius: {
        custom: `${data.customization.tailor_landing_border_radius}px`,
        button: `${data.customization.on_chat_buttons_radius}px`,
      },
      borderWidth: {
        buttonWidth: data.customization.is_open_button_border
          ? `${data.customization.open_button_border_width}px`
          : '0px',
      },
      borderColor: {
        buttonColor: data.customization.is_open_button_border
          ? data.customization.open_button_border_color
          : 'transparent',
      },
    },
  };

  if (webChatVersion === ChatVersion.v4_1) {
    const optionBackgroundColor = data.customization.fe_config?.virtualAssistant?.options?.optionBackgroundColor;
    const selectedOptionBackgroundColor =
      data.customization.fe_config?.virtualAssistant?.options?.selectedOptionBackgroundColor;

    theme.extend.colors.brand.panelTop = data.customization.panel_color_top;
    theme.extend.colors.brand.panelBottom = data.customization.panel_color_bottom;
    theme.extend.colors.brand.closeButtonBackground = data.customization.panel_close_button_background_color;
    theme.extend.colors.brand.closeButtonX = data.customization.panel_close_button_X_color;

    if (optionBackgroundColor) {
      theme.extend.colors.virtualAssistant.optionBackgroundColor = optionBackgroundColor;
    }
    if (selectedOptionBackgroundColor) {
      theme.extend.colors.virtualAssistant.selectedOptionBackgroundColor = selectedOptionBackgroundColor;
    }

    theme.extend.fontWeight.sectionHeader = '600';
  }

  return defineConfig({
    preflight: ({ theme }) => css`
      #root {
        color: ${webChatVersion === ChatVersion.v4_1 ? '#333535' : '#000000'};
        font-family: ${Array.isArray(theme('fontFamily.sans'))
          ? (theme('fontFamily.sans') as string[]).join(', ')
          : (theme('fontFamily.sans') as string)};
      }
    `,
    darkMode: 'class',
    presets: [presetAutoprefix(), presetTailwind()],
    rules: [
      [
        'wrap-anywhere',
        {
          overflowWrap: 'anywhere',
        },
      ],
      [
        /^line-clamp-(\d+)$/,
        (match) => {
          const lineClamp = match[1];
          return {
            display: '-webkit-box',
            '-webkit-line-clamp': lineClamp,
            '-webkit-box-orient': 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          };
        },
      ],
      [
        /^faq-button-(primary|secondary|disabled)$/,
        (match) => {
          const buttonType = match[1] as 'primary' | 'secondary' | 'disabled';
          const customization = data.customization;

          return {
            borderRadius: `${customization[`faq_${buttonType}_button_radius`]}px`,
            backgroundColor: customization[`faq_${buttonType}_button_background_color`],
            boxShadow: customization[`is_faq_${buttonType}_button_shadow`]
              ? customization[`faq_${buttonType}_button_shadow`]
              : 'none',
            border: customization[`is_faq_${buttonType}_button_border`]
              ? `${customization[`faq_${buttonType}_button_border_width`]}px solid ${
                  customization[`faq_${buttonType}_button_border_color`]
                }`
              : 'none',
            padding: `${customization[`faq_${buttonType}_button_top_bottom_padding`]}px ${
              customization[`faq_${buttonType}_button_right_side_padding`]
            }px ${customization[`faq_${buttonType}_button_top_bottom_padding`]}px ${
              customization[`faq_${buttonType}_button_left_side_padding`]
            }px`,
            fontSize: `${customization[`faq_${buttonType}_button_font_size`]}px`,
            lineHeight: `${customization[`faq_${buttonType}_button_font_line`]}px`,
            color: customization[`faq_${buttonType}_button_text_color`],
            textAlign: customization[`faq_${buttonType}_button_text_alignment`] as Property.TextAlign,
            justifyContent: (() => {
              switch (customization[`faq_${buttonType}_button_text_alignment`]) {
                case 'left':
                  return 'flex-start';
                case 'right':
                  return 'flex-end';
                default:
                  return 'center';
              }
            })(),
            flexDirection: customization[`faq_${buttonType}_button_icon_position`] === 'left' ? 'row' : 'row-reverse',
            background:
              customization[`faq_${buttonType}_button_background_type`] === BackgroundStyleType.GRADIENT
                ? `linear-gradient(${
                    customization[`faq_${buttonType}_button_gradient_type`] === GradientDirectionType.TOP_BOTTOM
                      ? 'to bottom'
                      : 'to right'
                  }, ${customization[`faq_${buttonType}_button_gradient_start`]}, ${
                    customization[`faq_${buttonType}_button_gradient_end`]
                  })`
                : undefined,
            '& > img': {
              width: customization[`faq_${buttonType}_button_icon_width`]
                ? `${customization[`faq_${buttonType}_button_icon_width`]}px`
                : undefined,
              height: customization[`faq_${buttonType}_button_icon_height`]
                ? `${customization[`faq_${buttonType}_button_icon_height`]}px`
                : undefined,
              marginRight: customization[`faq_${buttonType}_button_icon_position`] === 'left' ? '8px' : undefined,
              marginLeft: customization[`faq_${buttonType}_button_icon_position`] === 'left' ? undefined : '8px',
            },
          };
        },
      ],
      [
        'hide-scrollbars',
        {
          '&::-webkit-scrollbar': {
            display: 'none',
            width: '0px',
            height: '0px',
          },
          '&::-webkit-scrollbar-thumb': {
            display: 'none',
            width: '0px',
            height: '0px',
          },
          scrollbarWidth: 'none',
        },
      ],
      [
        'wrapper-position-desktop',
        {
          position: 'fixed',
          bottom: '0px',
          ...(data.customization.desktop_position.active === PlacementType.LEFT && { left: '0px' }),
          ...(data.customization.desktop_position.active === PlacementType.RIGHT && { right: '0px' }),
        },
      ],
      [
        'widget-position-mobile',
        {
          position: 'fixed',
          bottom: `${data.customization.mobile_position.bottom}px`,
          ...(data.customization.open_button_style === ButtonStyleType.TAILOR ||
          data.customization.mobile_position.active === PlacementType.RIGHT
            ? {
                right: `${data.customization.mobile_position.right}px`,
                left: 'unset',
              }
            : {
                left: `${data.customization.mobile_position.left}px`,
                right: 'unset',
              }),
        },
      ],
      [
        'widget-position-desktop',
        {
          position: 'fixed',
          bottom: `${data.customization.desktop_position.bottom}px`,
          ...(data.customization.open_button_style === ButtonStyleType.TAILOR ||
          data.customization.desktop_position.active === PlacementType.RIGHT
            ? {
                right: `${data.customization.desktop_position.right}px`,
                left: 'unset',
              }
            : {
                left: `${data.customization.desktop_position.left}px`,
                right: 'unset',
              }),
        },
      ],
      [
        'truncate-2-lines',
        {
          display: '-webkit-box',
          '-webkit-line-clamp': '2',
          '-webkit-box-orient': 'vertical',
          overflow: 'hidden',
        },
      ],
      [
        'close-chat-bubble',
        {
          backgroundColor: data.customization.close_button_background_color,
          '& > img': {
            height: `${data.customization.close_button_icon_height}px`,
            width: `${data.customization.close_button_icon_width}px`,
          },
        },
      ],
      [
        'hide-number-input-arrows',
        {
          '&::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: '0px',
          },
          '&::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 'px',
          },
          "&[type='number']": {
            '-moz-appearance': 'textfield',
          },
        },
      ],
      [
        'hide-select-arrow',
        {
          '-webkit-appearance': 'none',
          '-moz-appearance': 'none',
        },
      ],
      [
        'contact-form-header',
        {
          backgroundColor: data.customization.contact_forms_header_background_color || data.customization.main_color,
          borderTopRightRadius: `${data.customization.contact_forms_header_radius}px`,
          borderTopLeftRadius: `${data.customization.contact_forms_header_radius}px`,
        },
      ],
      [
        'text-highlight',
        {
          color: webChatVersion === ChatVersion.v4_1 ? '#333535' : '#295D97',
          fontWeight: '600',
        },
      ],
    ],
    theme,
  });
}

export default async function startTwind(data: HelpCenterResponse, flags: FeatureFlags) {
  const { webchatVersion } = await waitForIDs();
  const config = createTwindConfig(data, flags, webchatVersion);
  const fonts = getFonts(data);

  const isSupported = isAdoptedStyleSheetsSupported();
  const useAdoptedStyles = getConfig('ENV') !== 'local' && isSupported;

  // Create separate CSSStyleSheet
  const styleElement = document.createElement('style');
  styleElement.id = 'ostro-twind-style';
  const sheet = useAdoptedStyles ? cssom(new CSSStyleSheet()) : dom(styleElement);

  // Use sheet and config to create an twind instance. `tw` will
  // append the right CSS to our custom stylesheet.
  // @ts-ignore
  const tw = twind(config, sheet);

  fonts.forEach(async (font) => {
    await font.load();

    document.fonts.add(font);
  });

  if (shadowRoot) {
    // link sheet target to shadow dom root
    if (useAdoptedStyles) {
      shadowRoot.adoptedStyleSheets = [sheet.target as CSSStyleSheet];
    } else {
      if (shadowRoot.getElementById('ostro-twind-style')) {
        shadowRoot.getElementById('ostro-twind-style')?.remove();
      }
      shadowRoot.appendChild(styleElement);
    }

    // add custom css
    if (shadowRoot.getElementById('ostro-custom-css')) {
      shadowRoot.getElementById('ostro-custom-css')?.remove();
    }
    const customCSSStyleElement = document.createElement('style');
    customCSSStyleElement.id = 'ostro-custom-css';
    customCSSStyleElement.innerHTML = data.customization.custom_css;
    shadowRoot.appendChild(customCSSStyleElement);

    // finally, observe using tw function
    observe(tw, shadowRoot);
  }
}
